import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  Alert,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../context/AuthContext";
import useFetchObjects from "../../api/useFetchObjects";

const ShopAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/shop", token);
  const { shop } = useTenant();
  const auth = useAuth();
  const currentUser = auth?.user?.user || auth?.user;
  const isSuperAdmin = Boolean(currentUser?.is_superuser);
  const shouldBlockCreate = Boolean(shop) && !isSuperAdmin;
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem("i18nextLng") ||
    "en"
  ).split("-")[0];
  const isRTL = ["ps", "dr"].includes(currentLanguage);
  const fieldDirection = isRTL ? "rtl" : "ltr";
  const fieldTextAlign = isRTL ? "right" : "left";
  const rtlTextFieldProps = {
    inputProps: {
      dir: fieldDirection,
      style: { textAlign: fieldTextAlign },
    },
    InputLabelProps: {
      sx: {
        direction: fieldDirection,
        right: isRTL ? 24 : "auto",
        left: isRTL ? "auto" : undefined,
        transformOrigin: isRTL ? "top right" : "top left",
      },
    },
    sx: {
      direction: fieldDirection,
      "& .MuiOutlinedInput-root": {
        direction: fieldDirection,
      },
      "& .MuiInputBase-input": {
        textAlign: fieldTextAlign,
      },
      "& .MuiInputLabel-root": {
        textAlign: fieldTextAlign,
      },
    },
  };
  const { data: usersData, isLoading: isUsersLoading } = useFetchObjects(
    ["shop-owner-users"],
    "admin/users/?page_size=200",
    token,
    { enabled: isSuperAdmin && open }
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");

  const users = usersData?.results || usersData || [];

  const handleSubmit = () => {
    if (shouldBlockCreate) {
      return;
    }
    const formData = {
      name: name.trim(),
      description,
    };
    if (isSuperAdmin && ownerId) {
      formData.owner = ownerId;
    }
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setDescription("");
      setOwnerId("");
      onClose();
    }
  }, [resetForm, onClose]);

  useEffect(() => {
    if (open && isSuperAdmin) {
      setOwnerId("");
    }
  }, [open, isSuperAdmin]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("shop.addShop")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          {shouldBlockCreate ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("shop.shopAlreadyExists", "This account already has a branch. You can edit it instead of creating a new one.")}
            </Alert>
          ) : null}
          {isSuperAdmin ? (
            <TextField
              select
              margin="dense"
              label={t("shop.ownerUser", "Select User")}
              fullWidth
              required
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              disabled={isUsersLoading}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: { direction: fieldDirection, textAlign: fieldTextAlign },
                  },
                },
              }}
              {...rtlTextFieldProps}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={String(user.id)}>
                  {user.username || user.email || `User ${user.id}`}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label={t("shop.shopName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={shouldBlockCreate}
            {...rtlTextFieldProps}
          />
          <TextField
            margin="dense"
            label={t("shop.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={shouldBlockCreate}
            {...rtlTextFieldProps}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("shop.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || shouldBlockCreate || (isSuperAdmin && !ownerId)}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("shop.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopAdd;
